/**
 * Landing page discount code manager & scroll-to-cart handler.
 *
 * Config is read from a <script type="application/json" data-landing-config>
 * element on the page:
 *   { "discountCode": "MYCODE", "variantId": 123, "regularPrice": "$299.99",
 *     "offers": [{ "code": "FLASK10", "price": "$269.99" },
 *                { "code": "FLASK20", "price": "$239.99", "tracked": true }] }
 *
 * "codesDisabled": true (sale mode: the section has no default code and the
 * variant is on a compare-at sale, which blocks every code at checkout) means
 * NO code reaches checkout: the Liquid-rendered sale price and "N% discount
 * applied" labels stand, and any discount= in the link or button is stripped.
 *
 * Which code reaches checkout (highest precedence first):
 *   1. the page's configured discountCode — the offer this page advertises
 *   2. a ?discount= param on the landing URL, only when the page has no code
 *   3. a discount_code cookie, only when neither of the above is set
 *
 * Affiliate offers (pages whose config has a variantId) replace that with,
 * highest precedence first:
 *   1. a code in the link: ?discount=CODE, or the discount_code cookie that
 *      Shopify's /discount/CODE links set. Affiliates with their own codes
 *      keep them. On a tracked visit the untracked default code (FLASK10) in
 *      the link does not count, so it can't undercut the tracked offer.
 *   2. the tracked offer (FLASK20) when the visitor arrived on an Impact,
 *      GoAffPro or Everflow tracking link (TRACKING_PARAMS). That is
 *      remembered for TRACKED_DAYS in a first-party cookie; GoAffPro's own
 *      30-day ref cookie counts too (isGoaffproVisitor).
 *   3. the configured discountCode (FLASK10).
 * All of this is decided in the browser with no network call: the page never
 * creates a cart just to look at a code, because Shopify counts every Storefront
 * cartCreate as an add-to-cart. The house codes in "offers" are priced by
 * Liquid. Any other code shows the regular price and is checked against the
 * store (a tokenless Storefront cart) only when the shopper clicks Checkout,
 * the moment a cart gets made anyway. If Shopify rejects it, or doesn't answer
 * within CLICK_WAIT_MS, checkout gets the tracked or default code instead, so
 * an expired affiliate code never leaves the shopper with no discount.
 *
 * Exactly ONE discount param is sent. Any discount= already on the button or on
 * the landing URL is removed first. Shopify honours the LAST duplicate discount
 * param, which previously let an affiliate link carrying ?discount=FLASK10, a
 * stale discount_code cookie, or a later script rewrite silently replace the
 * page's offer at checkout. Every other landing-URL param (utm_*, ref,
 * irclickid, _ef_*, ...) is still forwarded so affiliate attribution survives.
 *
 * Settling adds .offer-ready to <html>, which the section's CSS uses to reveal
 * the prices and the "code X applied" labels.
 *
 * If no config element exists, it still initialises the scroll-to-cart handler.
 */
(function () {
  var DISCOUNT_KEY = 'discount';

  var STOREFRONT_API = '/api/2025-07/graphql.json';
  var CLICK_WAIT_MS = 2000;

  // Query params each affiliate network adds to its tracking links. GoAffPro's
  // list is the live app config (goaffpro_identifiers).
  var TRACKING_PARAMS = [
    { network: 'impact', params: ['irclickid', 'im_ref'] },
    { network: 'goaffpro', params: ['gfp_ref', 'ref', 'aff', 'wpam_id', 'click_id'] },
    { network: 'everflow', params: ['_ef_transaction_id', 'affid'] }
  ];
  var TRACKED_COOKIE = 'echo_aff_src';
  var TRACKED_DAYS = 30;

  /**
   * GoAffPro keeps its referral for 30 days in a cookie named ref, next to a
   * gfp_ref_expires cookie. Requiring both keeps an unrelated "ref" cookie
   * from counting as affiliate traffic.
   */
  function isGoaffproVisitor() {
    return !!(getCookie('ref') && getCookie('gfp_ref_expires'));
  }

  var CODE_QUERY =
    'mutation LandingCode($lines: [CartLineInput!], $codes: [String!], $country: CountryCode)' +
    ' @inContext(country: $country) {' +
    ' cartCreate(input: { lines: $lines, discountCodes: $codes }) { cart {' +
    ' discountCodes { code applicable }' +
    ' } } }';

  function getCookie(name) {
    if (typeof document === 'undefined') return null;
    var value = '; ' + document.cookie;
    var parts = value.split('; ' + name + '=');
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  function setCookie(name, value, days) {
    var expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = name + '=' + encodeURIComponent(value) + '; expires=' + expires + '; path=/; SameSite=Lax';
  }

  function decode(value) {
    try { return decodeURIComponent(value || ''); } catch (e) { return value || ''; }
  }

  /** "a=1&b=2" -> [{ key: 'a', raw: 'a=1' }, ...], order preserved, encoding untouched. */
  function parseQuery(query) {
    var out = [];
    if (!query) return out;
    query.replace(/^\?/, '').split('&').forEach(function (pair) {
      if (!pair) return;
      var i = pair.indexOf('=');
      out.push({ key: i === -1 ? pair : pair.slice(0, i), raw: pair });
    });
    return out;
  }

  function isDiscountKey(key) {
    return decode(key).toLowerCase() === DISCOUNT_KEY;
  }

  function discountFromQuery(query) {
    var found = '';
    parseQuery(query).forEach(function (p) {
      if (!isDiscountKey(p.key)) return;
      var i = p.raw.indexOf('=');
      found = decode(i === -1 ? '' : p.raw.slice(i + 1)); // last wins, matching Shopify
    });
    return found.trim();
  }

  /** Decide which code this page must send. */
  function resolveCode(configCode, pageQuery, cookieCode) {
    configCode = (configCode || '').trim();
    if (configCode) return configCode;
    var fromUrl = discountFromQuery(pageQuery);
    if (fromUrl) return fromUrl;
    return (cookieCode || '').trim();
  }

  /** The affiliate network whose tracking params are on the URL, or ''. */
  function trackingSource(pageQuery) {
    var keys = {};
    parseQuery(pageQuery).forEach(function (p) {
      var i = p.raw.indexOf('=');
      if (i !== -1 && p.raw.slice(i + 1)) keys[decode(p.key).toLowerCase()] = true;
    });
    for (var n = 0; n < TRACKING_PARAMS.length; n++) {
      if (TRACKING_PARAMS[n].params.some(function (k) { return keys[k]; })) return TRACKING_PARAMS[n].network;
    }
    return '';
  }

  /**
   * Pick the offer for this visit. Returns { code, price, verify, fallback }:
   * price is the Liquid-rendered price string, or null when the page can't
   * price the code; verify means the code must be checked at click and
   * fallback is the offer to use if it fails.
   */
  function chooseOffer(configCode, offers, pageQuery, cookieCode, tracked) {
    var configKey = (configCode || '').trim().toUpperCase();
    var byCode = {};
    var trackedOffer = null;
    (offers || []).forEach(function (o) {
      byCode[o.code.toUpperCase()] = o;
      if (o.tracked) trackedOffer = o;
    });

    var base = tracked && trackedOffer ? trackedOffer : byCode[configKey];
    var fallback = base
      ? { code: base.code, price: base.price, verify: false }
      : { code: (configCode || '').trim(), price: null, verify: false };

    var linked = discountFromQuery(pageQuery) || decode(cookieCode).trim();
    var key = linked.toUpperCase();
    // The default code in the link changes nothing: untracked it is the
    // fallback already, tracked it must not undercut the tracked offer.
    if (!linked || key === fallback.code.toUpperCase() || key === configKey) return fallback;
    if (byCode[key]) return { code: byCode[key].code, price: byCode[key].price, verify: false };
    return { code: linked, price: null, verify: true, fallback: fallback };
  }

  /** Resolves true/false for whether Shopify accepts the code, or null on failure or timeout. */
  function checkCode(code, variantId, timeoutMs) {
    if (typeof fetch === 'undefined') return Promise.resolve(null);
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = setTimeout(function () { if (controller) controller.abort(); }, timeoutMs);
    var country = (window.Shopify && window.Shopify.country) || null;
    return fetch(STOREFRONT_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        query: CODE_QUERY,
        variables: {
          lines: [{ merchandiseId: 'gid://shopify/ProductVariant/' + variantId, quantity: 1 }],
          codes: [code],
          country: country
        }
      }),
      signal: controller ? controller.signal : undefined
    })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (json) {
        var cart = json && json.data && json.data.cartCreate && json.data.cartCreate.cart;
        if (!cart) return null;
        return (cart.discountCodes || []).some(function (d) { return d.applicable; });
      })
      .catch(function () { return null; })
      .then(function (ok) {
        clearTimeout(timer);
        return ok;
      });
  }

  function setShown(el, shown) {
    el.style.display = shown ? '' : 'none';
  }

  /** Swap the page's offer copy and prices to the chosen offer. */
  function showOffer(offer, regularPrice) {
    var label = offer.code.toUpperCase();
    document.querySelectorAll('[data-offer-cta]').forEach(function (el) {
      el.textContent = offer.price ? 'Checkout · code ' + label + ' applied' : 'Checkout · code ' + label;
    });
    document.querySelectorAll('[data-offer-price]').forEach(function (el) {
      el.textContent = offer.price || regularPrice;
    });
    document.querySelectorAll('[data-offer-compare]').forEach(function (el) {
      el.textContent = regularPrice;
      setShown(el, !!offer.price);
    });
  }

  /**
   * Build the checkout href: strip every discount param from the button href
   * and the landing query, forward the rest, then append ours LAST.
   */
  function buildHref(href, code, pageQuery) {
    href = href || '';
    var hashIdx = href.indexOf('#');
    var hash = hashIdx === -1 ? '' : href.slice(hashIdx);
    var noHash = hashIdx === -1 ? href : href.slice(0, hashIdx);
    var qIdx = noHash.indexOf('?');
    var path = qIdx === -1 ? noHash : noHash.slice(0, qIdx);
    var params = [];

    parseQuery(qIdx === -1 ? '' : noHash.slice(qIdx + 1)).forEach(function (p) {
      if (!isDiscountKey(p.key)) params.push(p.raw);
    });
    // Skip landing params the href already carries, so rewriting twice is a no-op.
    parseQuery(pageQuery).forEach(function (p) {
      if (!isDiscountKey(p.key) && params.indexOf(p.raw) === -1) params.push(p.raw);
    });
    if (code) params.push(DISCOUNT_KEY + '=' + encodeURIComponent(code));

    return path + (params.length ? '?' + params.join('&') : '') + hash;
  }

  function init() {
    var root = document.documentElement;
    if (root.hasAttribute('data-landing-discount-init')) return; // never bind twice
    root.setAttribute('data-landing-discount-init', '');

    /* ---- Scroll-to-cart ---- */
    var discoverButton = document.querySelector('.js-scroll-to');
    if (discoverButton) {
      discoverButton.addEventListener('click', function (e) {
        e.preventDefault();
        var cartButton = document.querySelector('.js-cta-btn');
        if (cartButton) cartButton.click();
      });
    }

    /* ---- Discount code manager ---- */
    var configEl = document.querySelector('script[data-landing-config]');
    var config = {};
    if (configEl) {
      try { config = JSON.parse(configEl.textContent) || {}; } catch (e) { /* ignore parse errors */ }
    }
    var configCode = config.discountCode || '';
    var variantId = config.variantId ? String(config.variantId) : '';
    var codesDisabled = config.codesDisabled === true;

    var pageQuery = window.location.search.slice(1);
    var tracked = '';
    var offerFor = function () {
      return chooseOffer(configCode, config.offers, pageQuery, getCookie('discount_code'), tracked);
    };
    var discountCode;
    if (codesDisabled) {
      discountCode = '';
    } else if (variantId) {
      tracked = trackingSource(pageQuery);
      if (tracked) setCookie(TRACKED_COOKIE, tracked, TRACKED_DAYS);
      else tracked = decode(getCookie(TRACKED_COOKIE)) || (isGoaffproVisitor() ? 'goaffpro' : '');
      var offer = offerFor();
      if (offer.code.toUpperCase() !== configCode.trim().toUpperCase()) showOffer(offer, config.regularPrice || '');
      // Links only ever carry a code the page can vouch for: an unchecked code
      // is swapped in on click, after Shopify accepts it, so a link opened in a
      // new tab still gets the tracked/default discount.
      discountCode = offer.verify ? offer.fallback.code : offer.code;
    } else {
      discountCode = resolveCode(configCode, pageQuery, getCookie('discount_code'));
    }
    root.classList.add('offer-ready');

    // Update discount label text
    var labels = document.querySelectorAll('.js-discount-code');
    labels.forEach(function (el) {
      el.textContent = discountCode ? discountCode.toUpperCase() + ' Applied + ' : '';
    });

    // Update checkout/CTA button hrefs
    var buttons = [];
    document.querySelectorAll('.js-cta-btn, .js-checkout-btn').forEach(function (btn) {
      if (btn.getAttribute('href')) buttons.push(btn);
    });
    buttons.forEach(function (btn) {
      btn.setAttribute('href', buildHref(btn.getAttribute('href') || '', discountCode, pageQuery));
    });

    var leaving = false;
    buttons.forEach(function (btn) {
      // Recompute from the live href on click so a later rewrite by another
      // script (affiliate widgets rewrite cart links) cannot change the code.
      // The offer is re-read so a discount_code cookie an affiliate app set
      // after load still counts. A code the page couldn't price is checked
      // now; a rejected or unanswered one falls back to the tracked/default
      // offer.
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (leaving) return;
        var go = function (code) {
          leaving = true;
          window.location.href = buildHref(btn.getAttribute('href') || '', code, pageQuery);
        };
        if (codesDisabled || !variantId) return go(discountCode);
        var now = offerFor();
        if (!now.verify) return go(now.code);
        leaving = true;
        checkCode(now.code, variantId, CLICK_WAIT_MS).then(function (ok) {
          go(ok ? now.code : now.fallback.code);
        });
      });
    });
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      parseQuery: parseQuery,
      resolveCode: resolveCode,
      buildHref: buildHref,
      trackingSource: trackingSource,
      chooseOffer: chooseOffer
    };
  } else if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
